import { Placeholder, Selection } from "@tiptap/extensions"
import { StarterKit } from "@tiptap/starter-kit"
import { Markdown } from "@tiptap/markdown"
// import { Mention } from "@tiptap/extension-mention"
import { TaskList } from "@tiptap/extension-list"
import { Color } from "@tiptap/extension-color"
import { Highlight } from "@tiptap/extension-highlight"
import { Mathematics } from "@tiptap/extension-mathematics"
import { Emoji, gitHubEmojis } from "@tiptap/extension-emoji"
import { Superscript } from "@tiptap/extension-superscript"
import { Subscript } from "@tiptap/extension-subscript"
import { TextAlign } from "@tiptap/extension-text-align"
import { Typography } from "@tiptap/extension-typography"
import { UniqueID } from "@tiptap/extension-unique-id"
import {
  getHierarchicalIndexes,
  TableOfContents,
} from "@tiptap/extension-table-of-contents"

import { getHuduSharedExtensions } from "@/tiptap/extensions/hudu-extensions"
import { UiState } from "@/tiptap/extensions/ui-state-extension"
import { HorizontalRule } from "@/tiptap/nodes/horizontal-rule-node/horizontal-rule-node-extension"
import { TaskItem } from "@/tiptap/nodes/task-item-node/task-item-node-extension"
import { Image } from "@/tiptap/nodes/image-node/image-node-extension"
import { InlineImage } from "@/tiptap/nodes/image-node/inline-image-node-extension"
import { Figure } from "@/tiptap/nodes/figure-node/figure-node-extension"
import { ImageUploadNode } from "@/tiptap/nodes/image-upload-node/image-upload-node-extension"
import { Indent } from "@/tiptap/extensions/indent-extension"
import { ListNormalizationExtension } from "@/tiptap/extensions/list-normalization-extension"
import { NodeAlignment } from "@/tiptap/extensions/node-alignment-extension"
import { NodeBackground } from "@/tiptap/extensions/node-background-extension"
import { TableHandleExtension } from "@/tiptap/nodes/table-node/extensions/table-handle"
import { TableCellMediaParagraph } from "@/tiptap/nodes/table-node/extensions/table-cell-media-paragraph"
import { TocNode } from "@/tiptap/nodes/toc-node/extensions/toc-node-extension"
import { Callout } from "@/tiptap/nodes/callout-node/callout-node-extension"
import { FontSize } from "@/tiptap/extensions/font-size-extension"
import FindAndReplace from "@tiptap/extension-find-and-replace"
import { FindReplaceUi } from "@/tiptap/extensions/find-replace-extension"
import { applyMarkdownHandlers } from "@/tiptap/core/markdown/markdown-handlers"
import { MarkdownPaste } from "@/tiptap/core/markdown/markdown-paste-extension"

export function buildEditorExtensions({
  placeholder = "Start writing...",
  onTocUpdate,
  imageUpload,
  tableOptions,
} = {}) {
  return applyMarkdownHandlers([
    // Markdown interchange (paste/import/export). Only overrides content
    // commands when a `contentType` option is passed, so the editor's default
    // HTML serialization is unaffected.
    Markdown,
    MarkdownPaste,
    StarterKit.configure({
      paragraph: false,
      listItem: false,
      orderedList: false,
      bulletList: false,
      codeBlock: false,
      horizontalRule: false,
      heading: {
        levels: [1, 2, 3, 4],
        HTMLAttributes: {
          style: null,
          class: null,
        },
      },
      bold: {
        HTMLAttributes: {
          style: null,
          class: null,
        },
      },
      italic: {
        HTMLAttributes: {
          style: null,
          class: null,
        },
      },
      dropcursor: {
        width: 2,
      },
      link: { openOnClick: false },
    }),
    ...getHuduSharedExtensions({ tableOptions }),
    HorizontalRule,
    TextAlign.configure({ types: ["heading", "paragraph"] }),
    Placeholder.configure({
      placeholder,
      emptyNodeClass: "is-empty with-slash",
    }),
    // Mention,
    Emoji.configure({
      emojis: gitHubEmojis.filter((emoji) => !emoji.name.includes("regional")),
      forceFallbackImages: true,
    }),
    NodeBackground.configure({
      types: [
        "paragraph",
        "heading",
        "blockquote",
        "taskList",
        "bulletList",
        "orderedList",
        "tableCell",
        "tableHeader",
        "tocNode",
      ],
    }),
    NodeAlignment,
    Mathematics,
    Superscript,
    Subscript,
    Indent,
    Color,
    FontSize,
    TaskList,
    TaskItem.configure({ nested: true }),
    Highlight.configure({ multicolor: true }),
    Selection,
    Image,
    InlineImage,
    Figure,
    Callout,
    TableOfContents.configure({
      getIndex: getHierarchicalIndexes,
      onUpdate(content) {
        onTocUpdate?.(content)
      },
    }),
    TableHandleExtension,
    TableCellMediaParagraph,
    ListNormalizationExtension,
    ImageUploadNode.configure({
      accept: "image/*",
      maxSize: imageUpload?.maxSize,
      limit: imageUpload?.limit ?? 3,
      upload: imageUpload?.upload,
      onError: imageUpload?.onError,
    }),
    UniqueID.configure({
      types: [
        "table",
        "paragraph",
        "bulletList",
        "orderedList",
        "taskList",
        "heading",
        "blockquote",
        "callout",
        "codeBlock",
        "tocNode",
      ],
    }),
    Typography,
    UiState,
    FindAndReplace.configure({
      injectCSS: false,
      searchDebounceMs: 0,
    }),
    FindReplaceUi,
    TocNode.configure({
      topOffset: 48,
    }),
  ])
}

export default buildEditorExtensions
